import axios from 'axios';
import {
  Grantha,
  GranthaL1,
  GranthaL2,
  GranthaL3,
  GranthaL4,
  GranthaL5,
  BulkCreateL1,
  BulkCreateL2,
  BulkCreateL3,
  BulkCreateL4,
  BulkCreateL5,
  GranthaSloka,
  CreateLinePayload,
  UpdateLinePayload,
  Language,
  Country,
  SankalpaLineBhed,
  BrahmanYajamanKartabyaLineBhed,
  BidhiLineBhed,
  Vyakhyanuvad,
  VyakhyanuvadWithDetails,
  SingleCreateL1,
  SingleCreateL2,
  SingleCreateL3,
  SingleCreateL4,
  SingleCreateL5,
  User,
  Policy,
  Effect,
  ShlokaWithVyakhyanuvad,
  Upodghat,
  ReferenceYJS,
  Sankalpa,
  CreateSankalpa,
  BrahmanYajamanKartabya,
  CreateBrahmanYajamanKartabya,
  Puja,
  CreatePuja,
  Samagri,
  CreateSamagri,
  Panchanga,
  BidhiLineImageAssociation,
  BidhiBhed,
  BidhiDefaultAmukPair,
  BidhiLineGranthaLink,
  CreateBidhiLineGranthaLinkPayload,
  BidhiLineGranthaLevelLink,
  CreateBidhiLineGranthaLevelLinkPayload,
  Festival,
  FestivalAssociation,
  FestivalBhed,
  FestivalNotificationPoint,
  FestivalTiming,
  ParentLevel,
  Media,
  MediaGroup,
  CreateMediaGroupDto,
  UpdateMediaGroupDto,
  UpdateMediaDto,
  BidhiSingleshotResponse,
} from '../types';
import type { BookDefaultStylesV1 } from '../utils/bookDefaults';
import type {
  ApprovedTiming,
  BidhiTiming,
  BookTiming,
  TimingConfig,
} from '../utils/timing';
import { API_URL, AUTH_URL } from '../config/apiUrls';
import type { AuthTokensResponse } from '../types/auth';
import type {
  CreatePopularBooksBidhiPayload,
  PopularBooksBidhi,
  UpdatePopularBooksBidhiPayload,
} from '../types/popularBooksBidhi';
import { attachAuthInterceptors } from './attachAuthInterceptors';
import { attachEncryptedResponseInterceptors } from './attachEncryptedResponseInterceptors';
import { unwrapApiList } from './encryptedApiResponse';
import { mapElibPanchangaRow, type ElibPanchangaRow } from '../utils/elibPanchanga';

export { api, API_URL, AUTH_URL };

// Must match axios baseURL so /uploads/* image paths resolve correctly in dev.

const api = axios.create({
  baseURL: API_URL,
});

/** Bare client for login / refresh / logout — no auth interceptors. */
const authClient = axios.create({
  baseURL: AUTH_URL,
  headers: { 'Content-Type': 'application/json' },
});

attachAuthInterceptors(api);
attachEncryptedResponseInterceptors(api);

// Grantha API calls
export type GranthaCatalogItem = Pick<
  Grantha,
  'id' | 'name' | 'abbreviation' | 'languageId' | 'profilePicture' | 'l1' | 'l2' | 'l3' | 'l4' | 'l5'
> & { defaultColor?: string | null };

export const granthaApi = {
  getAll: () => api.get<Grantha[]>('/grantha'),
  /** Lightweight selector list — prefer this over getAll/getAccessible for dropdowns. */
  getCatalog: () =>
    api.get<unknown>('/grantha/catalog').then((response) => {
      const unwrapped = unwrapApiList<GranthaCatalogItem>(response.data);
      return {
        ...response,
        data: unwrapped,
      };
    }),
  getAccessible: () =>
    // api.get<unknown>('/grantha/accessible').then((response) => {
    api.get<unknown>('/grantha').then((response) => {
      const unwrapped = unwrapApiList<Grantha>(response.data);
      if (process.env.NODE_ENV === 'development') {
        console.log('[grantha-api] getAccessible unwrapped list', unwrapped);
      }
      return {
        ...response,
        data: unwrapped,
      };
    }),
  getById: (id: number) => api.get<Grantha>(`/grantha/${id}`),
  /** Hierarchy tree without roles/vyakhyanuvads — preferred for viewer/hierarchy. */
  getHierarchy: (id: number) =>
    api.get<unknown>(`/grantha/${id}/hierarchy`).then((response) => {
      const data = response.data;
      // Encrypted interceptor usually returns the entity; tolerate wrapped shapes.
      const grantha =
        data && typeof data === 'object' && !Array.isArray(data) && 'data' in (data as object)
          ? ((data as { data: Grantha }).data ?? data)
          : data;
      return {
        ...response,
        data: grantha as Grantha,
      };
    }),
  create: (grantha: any) => {
    if (grantha instanceof FormData) {
      return api.post<Grantha>('/grantha', grantha, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
    }
    return api.post<Grantha>('/grantha', grantha);
  },
  update: (id: number, grantha: any) => {
    if (grantha instanceof FormData) {
      return api.patch<Grantha>(`/grantha/${id}`, grantha, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
    }
    return api.patch<Grantha>(`/grantha/${id}`, grantha);
  },
  delete: (id: number) => api.delete(`/grantha/${id}`),
  searchShlokas: (granthaId: number, text: string, languageId: number) =>
    api.get(`/grantha/${granthaId}/shloka/search`, { params: { text, languageId } }),
};

// GranthaL1 API calls
export const granthaL1Api = {
  getAllByGranthaId: (granthaId: number) => api.get<GranthaL1[]>(`/grantha-l1/grantha/${granthaId}`),
  getById: (id: number) => api.get<GranthaL1>(`/grantha-l1/${id}`),
  update: (id: number, l1: Partial<GranthaL1>) => api.patch<GranthaL1>(`/grantha-l1/${id}`, l1),
  delete: (id: number) => api.delete(`/grantha-l1/${id}`),
  bulkCreate: (data: BulkCreateL1) => api.post('/grantha-l1/bulk', data),
  singleCreate: (data: SingleCreateL1) => api.post(`/grantha-l1/single`, data),
};

// GranthaL2 API calls
export const granthaL2Api = {
  getAllByL1Id: (l1Id: number) => api.get<GranthaL2[]>(`/grantha-l2/l1/${l1Id}`),
  getById: (id: number) => api.get<GranthaL2>(`/grantha-l2/${id}`),
  update: (id: number, l2: Partial<GranthaL2>) => api.patch<GranthaL2>(`/grantha-l2/${id}`, l2),
  delete: (id: number) => api.delete(`/grantha-l2/${id}`),
  bulkCreate: (data: BulkCreateL2) => api.post('/grantha-l2/bulk', data),
  singleCreate: (data: SingleCreateL2) => api.post(`/grantha-l2/single`, data),
};

// GranthaL3 API calls
export const granthaL3Api = {
  getAllByL2Id: (l2Id: number) => api.get<GranthaL3[]>(`/grantha-l3/l2/${l2Id}`),
  getById: (id: number) => api.get<GranthaL3>(`/grantha-l3/${id}`),
  update: (id: number, l3: Partial<GranthaL3>) => api.patch<GranthaL3>(`/grantha-l3/${id}`, l3),
  delete: (id: number) => api.delete(`/grantha-l3/${id}`),
  bulkCreate: (data: BulkCreateL3) => api.post('/grantha-l3/bulk', data),
  singleCreate: (data: SingleCreateL3) => api.post(`/grantha-l3/single`, data),
};

// GranthaL4 API calls
export const granthaL4Api = {
  getAllByL3Id: (l3Id: number) => api.get<GranthaL4[]>(`/grantha-l4/l3/${l3Id}`),
  getById: (id: number) => api.get<GranthaL4>(`/grantha-l4/${id}`),
  update: (id: number, l4: Partial<GranthaL4>) => api.patch<GranthaL4>(`/grantha-l4/${id}`, l4),
  delete: (id: number) => api.delete(`/grantha-l4/${id}`),
  bulkCreate: (data: BulkCreateL4) => api.post('/grantha-l4/bulk', data),
  singleCreate: (data: SingleCreateL4) => api.post(`/grantha-l4/single`, data),
};

// GranthaL5 API calls
export const granthaL5Api = {
  getAllByL4Id: (l4Id: number) => api.get<GranthaL5[]>(`/grantha-l5/l4/${l4Id}`),
  getById: (id: number) => api.get<GranthaL5>(`/grantha-l5/${id}`),
  update: (id: number, l5: Partial<GranthaL5>) => api.patch<GranthaL5>(`/grantha-l5/${id}`, l5),
  delete: (id: number) => api.delete(`/grantha-l5/${id}`),
  bulkCreate: (data: BulkCreateL5) => api.post('/grantha-l5/bulk', data),
  singleCreate: (data: SingleCreateL5) => api.post(`/grantha-l5/single`, data),
};

// GranthaSloka API calls
export const shlokaApi = {
  getAll: () => api.get<GranthaSloka[]>('/grantha-shloka'),
  getById: (id: number) => api.get<GranthaSloka>(`/grantha-shloka/${id}`),
  getByGranthaId: (granthaId: number) => api.get<GranthaSloka[]>(`/grantha-shloka/grantha/${granthaId}`),
  getByLevel: (granthaId: number, level: string, levelId: number) =>
    api.get<GranthaSloka[][]>(`/grantha-shloka/grantha/${granthaId}/level/${level}/${levelId}`),
  getByLevelWithLanguage: (granthaId: number, level: string, levelId: number, languageId: number, signal?: AbortSignal) =>
    api.get<GranthaSloka[]>(`/grantha-shloka/grantha/${granthaId}/level/${level}/${levelId}/language/${languageId}`, { signal }),
  getByLevelsBatch: (
    granthaId: number,
    languageId: number,
    nodes: Array<{ level: string; levelId: number }>,
    signal?: AbortSignal,
  ) =>
    api.post<Record<string, GranthaSloka[]>>(
      `/grantha-shloka/grantha/${granthaId}/language/${languageId}/levels/batch`,
      {
        nodes: nodes.map((n) => ({
          level: n.level.toLowerCase(),
          levelId: n.levelId,
        })),
      },
      { signal },
    ),
  getByLevelWithVyakhyanuvad: (granthaId: number, level: string, levelId: number, languageId: number, vyakhyanuvadId: number) =>
    api.get<ShlokaWithVyakhyanuvad[]>(`/grantha-shloka/withvyakhyanuvad/grantha/${granthaId}/level/${level}/${levelId}/language/${languageId}/vyakhyanuvadId/${vyakhyanuvadId}`),
  getByGranthaShlokaNumberAndLanguage: (granthaId: number, shlokaCount: number, languageId: number) =>
    api.get<GranthaSloka[]>(`/grantha-shloka/grantha/${granthaId}/shloka/${shlokaCount}/language/${languageId}`),
  create: (shloka: GranthaSloka) => api.post<GranthaSloka>('/grantha-shloka', shloka),
  updateAnvaya: (shlokaNumberId: number, padachhedIds: string[]) =>
    api.post('/anvaya', { shlokaNumberId, padachhedIds }),
  reorder: (granthaId: number, orderedShlokaNumberIds: number[]) =>
    api.patch<Array<{ shlokaNumberId: number; number: number }>>('/grantha-shloka/reorder', {
      granthaId,
      orderedShlokaNumberIds,
    }),
  delete: (shlokaNumberId: number) => api.delete(`/grantha-shloka/${shlokaNumberId}`),
  deleteLink: (shlokaNumberId: number) => api.delete(`/grantha-shloka/link/${shlokaNumberId}`),
  createLine: (line: CreateLinePayload & { granthaId: number; parent_level: string; parent_level_id: number; shloka_count: number; shlokaNumberId: number; line_order: number; line_text: string }) =>
    api.post('/grantha-shloka/line', line),
  updateLine: (id: number, line: UpdateLinePayload) =>
    api.patch(`/grantha-shloka/${id}`, line),
  deleteLine: (id: number) => api.delete(`/grantha-shloka/line/${id}`),
  deleteLinkLine: (linkId: number, lineId: number, levelShlokaNumber?: number, order?: number) =>
    api.delete(`/grantha-shloka/link/line`, { data: { linkId, lineId, levelShlokaNumber, order } }),
  updateLinkLine: (linkId: number, lineId: number, order: number, line: UpdateLinePayload) =>
    api.patch(`/grantha-shloka/link/${linkId}/line/${lineId}/order/${order}`, line),
  link: (payload: {
    inside: {
      granthaId: string;
      parent_level: string;
      parent_level_id: string;
      shloka_count: string;
    };
    copy: {
      entire?: boolean;
      bhavartha: boolean;
      vyakhya: boolean;
      paddachhed: boolean;
      padartha: boolean;
      hyperlinks: boolean;
      vyakhyanuvad: boolean;
    };
    lines: {
      granthaId: string;
      parent_level: string;
      parent_level_id: string;
      shloka_count_id: string;
      order: string;
      level_shloka_number: string;
    }[];
  }) => api.post('/grantha-shloka/link', payload),
  addMetadata: (shlokaNumberId: number, metadata: Record<string, string>) =>
    api.post(`/grantha-shloka/metadata/${shlokaNumberId}`, metadata),
  deleteMetadata: (shlokaNumberId: number, key: string) =>
    api.delete(`/grantha-shloka/metadata/${shlokaNumberId}/key/${key}`),
  deleteShlokaSearchable: (shlokaCountId: number, key: string) =>
    api.delete(`/grantha-shloka/searchable/${shlokaCountId}/key/${key}`),
  addShlokaSearchable: (shlokaCountId: number, searchableArray: string[]) =>
    api.post(`/grantha-shloka/searchable/${shlokaCountId}`, searchableArray),
  updatePratik: (shlokaCountId: number, data: { pratik?: string; sar_pratik?: string }) =>
    api.patch(`/grantha-shloka/pratik/${shlokaCountId}`, data),
};



// Padachhed API calls
export const padachhedApi = {
  create: (padachhed: { shlokaNumberId: number; text: string; sar_text?: string; searchable?: string[]; order: number }) =>
    api.post('/padachhed', padachhed),
  update: (id: number, padachhed: { text: string; sar_text?: string; searchable?: string[] }) =>
    api.patch(`/padachhed/${id}`, padachhed),
  delete: (id: number) => api.delete(`/padachhed/${id}`),
  getByShlokaNumberId: (shlokaNumberId: number) =>
    api.get(`/padachhed/shloka/${shlokaNumberId}`),
  bulkUpdateOrder: (orders: { padachhedId: number; changedOrder: number }[]) =>
    api.patch('/padachhed/bulk-order', orders),
};

// Extra Anvaya API calls
export const extraAnvayaApi = {
  create: (extraAnvaya: { shlokaNumberId: number; text: string; order: number }) =>
    api.post('/extra-anvaya', extraAnvaya),
  update: (id: number, extraAnvaya: { text: string; order: number }) =>
    api.patch(`/extra-anvaya/${id}`, extraAnvaya),
  getByShlokaNumber: (shlokaNumber: number) =>
    api.get(`/extra-anvaya/shloka/${shlokaNumber}`),
  updateBulkOrder: (orders: { id: number; order: number }[]) =>
    api.patch('/extra-anvaya/bulk-order', orders),
  delete: (id: number) => api.delete(`/extra-anvaya/${id}`),
};

// Language API calls
export const languageApi = {
  getAll: () => api.get<Language[]>('/language'),
  getById: (id: number) => api.get<Language>(`/language/${id}`),
  create: (language: Language) => api.post<Language>('/language', language),
  update: (id: number, language: Partial<Language>) => api.patch<Language>(`/language/${id}`, language),
  delete: (id: number) => api.delete(`/language/${id}`),
};

// Country API calls
export const countryApi = {
  getAll: () => api.get<Country[]>('/country'),
  getById: (id: number) => api.get<Country>(`/country/${id}`),
  create: (country: Pick<Country, 'name'>) => api.post<Country>('/country', country),
  update: (id: number, country: Pick<Country, 'name'>) => api.patch<Country>(`/country/${id}`, country),
  delete: (id: number) => api.delete(`/country/${id}`),
};

// Sankalpa Line Bhed API calls
export const sankalpaLineBhedApi = {
  getBySankalpaLineId: (sankalpaLineId: number) =>
    api.get<SankalpaLineBhed[]>('/sankalpa-line-bhed', { params: { sankalpaLineId } }),
  getById: (id: number) => api.get<SankalpaLineBhed>(`/sankalpa-line-bhed/${id}`),
  create: (payload: {
    sankalpaLineId: number;
    text: string;
    languageId: number;
    countryId: number;
  }) => api.post<SankalpaLineBhed>('/sankalpa-line-bhed', payload),
  update: (
    id: number,
    payload: Partial<Pick<SankalpaLineBhed, 'text' | 'languageId' | 'countryId'>>,
  ) => api.patch<SankalpaLineBhed>(`/sankalpa-line-bhed/${id}`, payload),
  delete: (id: number) => api.delete(`/sankalpa-line-bhed/${id}`),
};

export const brahmanYajamanKartabyaLineBhedApi = {
  getByBrahmanYajamanKartabyaLineId: (brahmanYajamanKartabyaLineId: number) =>
    api.get<BrahmanYajamanKartabyaLineBhed[]>('/brahman-yajaman-kartabya-line-bhed', {
      params: { brahmanYajamanKartabyaLineId },
    }),
  getById: (id: number) =>
    api.get<BrahmanYajamanKartabyaLineBhed>(`/brahman-yajaman-kartabya-line-bhed/${id}`),
  create: (payload: {
    brahmanYajamanKartabyaLineId: number;
    text: string;
    languageId: number;
    countryId: number;
  }) => api.post<BrahmanYajamanKartabyaLineBhed>('/brahman-yajaman-kartabya-line-bhed', payload),
  update: (
    id: number,
    payload: Partial<Pick<BrahmanYajamanKartabyaLineBhed, 'text' | 'languageId' | 'countryId'>>,
  ) =>
    api.patch<BrahmanYajamanKartabyaLineBhed>(`/brahman-yajaman-kartabya-line-bhed/${id}`, payload),
  delete: (id: number) => api.delete(`/brahman-yajaman-kartabya-line-bhed/${id}`),
};

// Bidhi Line Bhed API calls
export const bidhiLineBhedApi = {
  getByBidhiLineId: (bidhiLineId: number) =>
    api.get<BidhiLineBhed[]>('/bidhi-line-bhed', { params: { bidhiLineId } }),
  getById: (id: number) => api.get<BidhiLineBhed>(`/bidhi-line-bhed/${id}`),
  create: (payload: {
    bidhiLineId: number;
    text: string;
    description?: string | null;
    languageId: number;
  }) => api.post<BidhiLineBhed>('/bidhi-line-bhed', payload),
  update: (
    id: number,
    payload: Partial<Pick<BidhiLineBhed, 'text' | 'description' | 'languageId'>>,
  ) => api.patch<BidhiLineBhed>(`/bidhi-line-bhed/${id}`, payload),
  delete: (id: number) => api.delete(`/bidhi-line-bhed/${id}`),
};

// Bidhi default amuk pair API calls
export const bidhiDefaultAmukPairApi = {
  listByBidhiId: (bidhiId: number) =>
    api.get<BidhiDefaultAmukPair[]>('/bidhi-default-amuk-pair', { params: { bidhiId } }),
  getById: (id: number) => api.get<BidhiDefaultAmukPair>(`/bidhi-default-amuk-pair/${id}`),
  create: (payload: { bidhiId: number; key: string; value: string }) =>
    api.post<BidhiDefaultAmukPair>('/bidhi-default-amuk-pair', payload),
  update: (id: number, payload: Partial<Pick<BidhiDefaultAmukPair, 'key' | 'value'>>) =>
    api.patch<BidhiDefaultAmukPair>(`/bidhi-default-amuk-pair/${id}`, payload),
  delete: (id: number) => api.delete(`/bidhi-default-amuk-pair/${id}`),
};

// Bidhi (level) Bhed API calls
export const bidhiBhedApi = {
  listByBidhiId: (bidhiId: number) =>
    api.get<BidhiBhed[]>('/bidhi-bhed', { params: { bidhiId } }),
  getById: (id: number) => api.get<BidhiBhed>(`/bidhi-bhed/${id}`),
  create: (payload: {
    bidhiId: number;
    languageId: number;
    name: string;
    description?: string | null;
    note?: string | null;
    bishesh?: string | null;
    nisedh?: string | null;
  }) => api.post<BidhiBhed>('/bidhi-bhed', payload),
  update: (
    id: number,
    payload: Partial<Pick<BidhiBhed, 'languageId' | 'name' | 'description' | 'note' | 'bishesh' | 'nisedh'>>,
  ) => api.patch<BidhiBhed>(`/bidhi-bhed/${id}`, payload),
  delete: (id: number) => api.delete(`/bidhi-bhed/${id}`),
};



// Bhavartha API calls
export const bhavarthaApi = {
  create: (bhavartha: { shlokaNumberId: number; text: string; languageId: number }) =>
    api.post('/bhavartha', bhavartha),
  update: (id: number, bhavartha: { text: string }) =>
    api.patch(`/bhavartha/${id}`, bhavartha),
  delete: (id: number) => api.delete(`/bhavartha/${id}`),
};

// Vyakhya API calls
export const vyakhyaApi = {
  create: (vyakhya: { shlokaNumberId: number; text: string; languageId: number }) =>
    api.post('/vyakhya', vyakhya),
  update: (id: number, vyakhya: { text: string }) =>
    api.patch(`/vyakhya/${id}`, vyakhya),
  delete: (id: number) => api.delete(`/vyakhya/${id}`),
};

// Padachhed Padartha API calls
export const padachhedPadarthaApi = {
  create: (padartha: { padachhedId: number; text: string; languageId: number }) =>
    api.post('/padachhed-padartha', padartha),
  update: (padartha: { id: number; text: string }) =>
    api.patch('/padachhed-padartha', padartha),
  delete: (id: number) => api.delete(`/padachhed-padartha/${id}`),
};

// Extra Anvaya Padartha API calls
export const extraAnvayaPadarthaApi = {
  create: (padartha: { extraAnvayaId: number; text: string; languageId: number }) =>
    api.post('/extra-anvaya-padartha', padartha),
  update: (padartha: { id: number; text: string }) =>
    api.patch('/extra-anvaya-padartha', padartha),
  delete: (id: number) => api.delete(`/extra-anvaya-padartha/${id}`),
};

// Profile API calls
export const profileApi = {
  getAll: () => api.get('/profile'),
  getById: (id: number) => api.get(`/profile/${id}`),
  create: (profile: any) => {
    if (profile instanceof FormData) {
      return api.post('/profile', profile, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
    }
    return api.post('/profile', profile, {
      headers: {
        'Content-Type': 'application/json'
      }
    });
  },
  update: (id: number, profile: any) => api.patch(`/profile/${id}`, profile),
  delete: (id: number) => api.delete(`/profile/${id}`),
};

// Role API calls
export const roleApi = {
  getAll: () => api.get('/role'),
  getById: (id: number) => api.get(`/role/${id}`),
  create: (role: { name: string }) => api.post('/role', role),
  update: (id: number, role: { name: string }) => api.patch(`/role/${id}`, role),
  delete: (id: number) => api.delete(`/role/${id}`),
};

// Grantha Role Association API calls
export const granthaRoleAssociationApi = {
  create: (association: { profileId: number; roleId: number; granthaId: number }) =>
    api.post('/grantha-role-association', association),
  delete: (id: number) => api.delete(`/grantha-role-association/${id}`),
};

// Vyakhyanuvad Role Association API calls
export const vyakhyanuvadRoleAssociationApi = {
  create: (association: { profileId: number; roleId: number; vyakhyanuvadId: number }) =>
    api.post('/vyakhyanuvad-role-association', association),
  delete: (id: number) => api.delete(`/vyakhyanuvad-role-association/${id}`),
};

// Bidhi Role Association API calls
export const bidhiRoleAssociationApi = {
  create: (association: { profileId: number; roleId: number; bidhiId: number }) =>
    api.post('/bidhi-role-association', association),
  delete: (id: number) => api.delete(`/bidhi-role-association/${id}`),
};

// Vyakhyanuvad API calls
export const vyakhyanuvadApi = {
  getAll: () => api.get<Vyakhyanuvad[]>('/vyakhyanuvad'),
  getById: (id: number) => api.get<Vyakhyanuvad>(`/vyakhyanuvad/${id}`),
  getByGranthaIdVyakhyanuvadId: (granthaId: number, vyakhyanuvadId: number) => api.get<VyakhyanuvadWithDetails>(`/vyakhyanuvad/grantha/${granthaId}/vyakhyanuvad/${vyakhyanuvadId}`),
  getByGranthaId: (granthaId: number) => api.get<Vyakhyanuvad[]>(`/vyakhyanuvad/grantha/${granthaId}`),
  create: (vyakhyanuvad: { name: any; granthaId: any; languageId: any; metadata: Record<any, any>; }) => api.post<Vyakhyanuvad>('/vyakhyanuvad', vyakhyanuvad),
  update: (id: number, vyakhyanuvad: Partial<Vyakhyanuvad>) => api.patch<Vyakhyanuvad>(`/vyakhyanuvad/${id}`, vyakhyanuvad),
  delete: (id: number) => api.delete(`/vyakhyanuvad/${id}`),
};

// Vyakhyanuvad Text API calls
export const vyakhyanuvadTextApi = {
  create: (vyakhyanuvadText: { shlokaNumberId: number; vyakhyanuvadId: number; text: string }) =>
    api.post('/vyakhyanuvad-text', vyakhyanuvadText),
  update: (id: number, vyakhyanuvadText: { text: string }) =>
    api.put(`/vyakhyanuvad-text/${id}`, vyakhyanuvadText),
  updateUpodghat: (id: number, upodghat: Partial<Upodghat>) =>
    api.put(`/vyakhyanuvad-text/${id}/upodghat`, upodghat),
  delete: (id: number) => api.delete(`/vyakhyanuvad-text/${id}`),
  getByShlokaAndVyakhyanuvad: (shlokaNumberId: number, vyakhyanuvadId: number) =>
    api.get(`/vyakhyanuvad-text/shloka/${shlokaNumberId}/vyakhyanuvad/${vyakhyanuvadId}`),
};

// Vyakhyanuvad Translation API calls
export const vyakhyanuvadTranslationApi = {
  create: (vyakhyanuvadTranslation: { shlokaNumberId: number; vyakhyanuvadTextId: number; text: string; languageId: number }) =>
    api.post('/vyakhyanuvad-translation', vyakhyanuvadTranslation),
  update: (id: number, vyakhyanuvadTranslation: { text: string; languageId?: number }) =>
    api.put(`/vyakhyanuvad-translation/${id}`, vyakhyanuvadTranslation),
  delete: (id: number) => api.delete(`/vyakhyanuvad-translation/${id}`),
  getByShlokVyakhyanuvadAndLanguage: (shlokaNumberId: number, vyakhyanuvadId: number, languageId: number) =>
    api.get(`/vyakhyanuvad-translation/shloka/${shlokaNumberId}/vyakhyanuvad/${vyakhyanuvadId}/language/${languageId}`),
};

// Auth API calls (central auth)
export const authApi = {
  login: (credentials: { email: string; password: string }) =>
    authClient.post<AuthTokensResponse>('/auth/login', credentials),
  refresh: (refresh_token: string) =>
    authClient.post<AuthTokensResponse>('/auth/refresh', { refresh_token }),
  logout: (refresh_token: string) =>
    authClient.post('/auth/logout', { refresh_token }),
};

// User API calls
export const userApi = {
  getAll: () => api.get<User[]>('/auth'),
  create: (user: { username: string; email: string; password: string }) => api.post<User>('/auth', user),
  update: (id: number, user: Partial<User>) => api.patch<User>(`/auth/${id}`, user),
  resetPassword: (username: string, newPassword: string) => api.post('/auth/reset-password', { username, newPassword }),
  delete: (id: number) => api.delete(`/auth/${id}`),
  deactivate: (id: number) => api.patch(`/auth/${id}/deactivate`, {}),
  activate: (id: number) => api.patch(`/auth/${id}/activate`, {}),
  assignPolicy: (userId: number, policyId: number) => api.post('/auth/assign-policy', { userId, policyId }),
};

// Policy API calls
export const policyApi = {
  getAll: () => api.get<Policy[]>('/auth/policy'),
  create: (policy: { name: string; effect: Effect; action: string; resource: string }) => api.post<Policy>('/auth/policy', policy),
  update: (id: number, policy: { name: string; effect: Effect; action: string; resource: string }) => api.patch<Policy>(`/auth/policy/${id}`, policy),
  delete: (id: number) => api.delete(`/auth/policy/${id}`),
};

// Reference API calls
export const referenceApi = {
  create: (reference: ReferenceYJS) => api.post<ReferenceYJS>('/references', reference),
  update: (id: number, reference: Partial<ReferenceYJS>) => api.patch<ReferenceYJS>(`/references/${id}`, reference),
  delete: (id: number) => api.delete(`/references/${id}`),
};

//Bidhi API Calls
export const karmakandaAPI = {
  createBidhi: (bidhi: any) => {
    if (bidhi instanceof FormData) {
      return api.post('/bidhi', bidhi, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
    }
    return api.post('/bidhi', bidhi);
  },
  updateBidhi: (id: number, bidhi: any) => {
    if (bidhi instanceof FormData) {
      return api.patch(`/bidhi/${id}`, bidhi, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
    }
    return api.patch(`/bidhi/${id}`, bidhi);
  },
  /** Full nested list (mobile/legacy). Prefer getBidhiCatalog / getAllBidhisDeduped on web. */
  getAllBidhis: () => api.get('/bidhi'),
  /** Smaller list payload via server-side dedupe maps. */
  getAllBidhisDeduped: () => api.get('/bidhi', { params: { dedupe: 1 } }),
  /** Lightweight id/name catalog for selectors — does not load lines/children. */
  getBidhiCatalog: () => api.get('/bidhi/catalog'),
  getRootsBidhis: () => api.get('/bidhi/roots'),
  getBidhiById: (id: number, options?: { dedupe?: boolean }) =>
    api.get(`/bidhi/${id}`, options?.dedupe ? { params: { dedupe: 1 } } : undefined),
  getBidhiSingleshot: async (id: number) => {
    try {
      return await api.get<BidhiSingleshotResponse>(`/bidhisingleshot/${id}`);
    } catch (err: any) {
      if (err?.response?.status === 404) {
        return await api.get<BidhiSingleshotResponse>(`/bidhi/singleshot/${id}`);
      }
      throw err;
    }
  },
  deleteBidhiById: (id: number) => api.delete(`/bidhi/${id}`),
  addParent: (childId: number, parentId: number, data: { order: number, startingPosition: number, lineId: number, selectedBidhiLineIds: Array<number>, entireBidhi: boolean, isTitleHidden?: boolean }) =>
    api.post(`/bidhi/${childId}/parent/${parentId}`, data),
  removeParent: (childId: number, parentId: number, lineId: number) =>
    api.delete(`/bidhi/${childId}/parent/${parentId}`, { data: { lineId } }),
  createGranthaReference: (dto: any) => api.post('/bidhi/grantha-reference', dto),
  updateGranthaReference: (id: number, dto: any) =>
    api.patch(`/bidhi/grantha-reference/${id}`, dto),
  deleteGranthaReference: (id: number) => api.delete(`/bidhi/grantha-reference/${id}`),
  upsertGranthaReferenceBhedPosition: (
    referenceId: number,
    dto: { bidhiLineBhedId: number; startPosition: number },
  ) => api.put(`/bidhi/grantha-reference/${referenceId}/bhed-position`, dto),
  deleteGranthaReferenceBhedPosition: (referenceId: number, bidhiLineBhedId: number) =>
    api.delete(`/bidhi/grantha-reference/${referenceId}/bhed-position/${bidhiLineBhedId}`),
  createBidhiHyperlinkBidhi: (dto: {
    bidhiId: number;
    lineId: number;
    startPosition: number;
    endingPosition: number;
    shortcut?: boolean;
    references: {
      bidhiId: number;
      entireBidhi?: boolean;
      selectedBidhiLineIds?: number[];
    }[];
  }) => api.post('/bidhi/bidhi-hyperlink-bidhi', dto),
  updateBidhiHyperlinkBidhi: (id: number, dto: Partial<{
    startPosition: number;
    endingPosition: number;
    shortcut: boolean;
    references: {
      bidhiId: number;
      entireBidhi?: boolean;
      selectedBidhiLineIds?: number[];
    }[];
  }>) => api.patch(`/bidhi/bidhi-hyperlink-bidhi/${id}`, dto),
  deleteBidhiHyperlinkBidhi: (id: number) =>
    api.delete(`/bidhi/bidhi-hyperlink-bidhi/${id}`),
  upsertBidhiHyperlinkBidhiBhedPosition: (
    hyperlinkId: number,
    dto: { bidhiLineBhedId: number; startPosition: number; endingPosition: number },
  ) => api.put(`/bidhi/bidhi-hyperlink-bidhi/${hyperlinkId}/bhed-position`, dto),
  deleteBidhiHyperlinkBidhiBhedPosition: (hyperlinkId: number, bidhiLineBhedId: number) =>
    api.delete(`/bidhi/bidhi-hyperlink-bidhi/${hyperlinkId}/bhed-position/${bidhiLineBhedId}`),
  createBidhiHyperlinkSankalpa: (dto: {
    bidhiId: number;
    lineId: number;
    startPosition: number;
    endingPosition: number;
    shortcut?: boolean;
    references: { sankalpaId: number }[];
  }) => api.post('/bidhi/bidhi-hyperlink-sankalpa', dto),
  updateBidhiHyperlinkSankalpa: (id: number, dto: Partial<{
    startPosition: number;
    endingPosition: number;
    shortcut: boolean;
    references: { sankalpaId: number }[];
  }>) => api.patch(`/bidhi/bidhi-hyperlink-sankalpa/${id}`, dto),
  deleteBidhiHyperlinkSankalpa: (id: number) =>
    api.delete(`/bidhi/bidhi-hyperlink-sankalpa/${id}`),
  upsertBidhiHyperlinkSankalpaBhedPosition: (
    hyperlinkId: number,
    dto: { bidhiLineBhedId: number; startPosition: number; endingPosition: number },
  ) => api.put(`/bidhi/bidhi-hyperlink-sankalpa/${hyperlinkId}/bhed-position`, dto),
  deleteBidhiHyperlinkSankalpaBhedPosition: (hyperlinkId: number, bidhiLineBhedId: number) =>
    api.delete(`/bidhi/bidhi-hyperlink-sankalpa/${hyperlinkId}/bhed-position/${bidhiLineBhedId}`),
  createBidhiHyperlinkGrantha: (dto: {
    bidhiId: number;
    lineId: number;
    startPosition: number;
    endingPosition: number;
    shortcut?: boolean;
    referenceType: string;
    references: {
      granthaId: number;
      parent_level: string;
      parent_level_id: number;
      selectedShlokaCountNumber: number;
      selectedShlokaNumberId: number;
      entireLine: boolean;
      lineIds?: number[];
      shlokaLines?: { id: number; line_text: string; line_order: number }[];
    }[];
    notes?: string;
    omOptions?: number;
  }) => api.post('/bidhi/bidhi-hyperlink-grantha', dto),
  updateBidhiHyperlinkGrantha: (id: number, dto: Partial<{
    startPosition: number;
    endingPosition: number;
    shortcut: boolean;
    referenceType: string;
    references: {
      granthaId: number;
      parent_level: string;
      parent_level_id: number;
      selectedShlokaCountNumber: number;
      selectedShlokaNumberId: number;
      entireLine: boolean;
      lineIds?: number[];
      shlokaLines?: { id: number; line_text: string; line_order: number }[];
    }[];
    notes?: string;
    omOptions?: number;
  }>) => api.patch(`/bidhi/bidhi-hyperlink-grantha/${id}`, dto),
  deleteBidhiHyperlinkGrantha: (id: number) =>
    api.delete(`/bidhi/bidhi-hyperlink-grantha/${id}`),
  upsertBidhiHyperlinkGranthaBhedPosition: (
    hyperlinkId: number,
    dto: { bidhiLineBhedId: number; startPosition: number; endingPosition: number },
  ) => api.put(`/bidhi/bidhi-hyperlink-grantha/${hyperlinkId}/bhed-position`, dto),
  deleteBidhiHyperlinkGranthaBhedPosition: (hyperlinkId: number, bidhiLineBhedId: number) =>
    api.delete(`/bidhi/bidhi-hyperlink-grantha/${hyperlinkId}/bhed-position/${bidhiLineBhedId}`),
  addLines: (id: number, lines: { content: string; order: number; description?: string | null; isVisible?: boolean; link_only_line?: boolean; metadata?: Record<string, unknown> }[]) =>
    api.post(`/bidhi/line/${id}`, lines),
  updateLine: (lineId: number, data: { content?: string; order?: number; description?: string | null; isVisible?: boolean; link_only_line?: boolean; metadata?: Array<any> }) =>
    api.patch(`/bidhi/line/${lineId}`, data),
  deleteLine: (bidhiId: number, lineId: number) =>
    api.delete(`/bidhi/line/${lineId}`),
  bulkUpdateBidhiLineOrder: (updates: { bidhiLineId: number; changedOrder: number }[]) =>
    api.patch('/bidhi/lines/bulk-order', updates),
};

export const bidhiLineImageAssociationApi = {
  listByBidhiLineId: (bidhiLineId: number) =>
    api.get<BidhiLineImageAssociation[]>('/bidhi-line-image-association', {
      params: { bidhiLineId },
    }),
  create: (formData: FormData) =>
    api.post<BidhiLineImageAssociation>('/bidhi-line-image-association', formData),
  update: (id: number, formData: FormData) =>
    api.patch<BidhiLineImageAssociation>(`/bidhi-line-image-association/${id}`, formData),
  delete: (id: number) => api.delete(`/bidhi-line-image-association/${id}`),
  upsertBhedPosition: (
    associationId: number,
    dto: { bidhiLineBhedId: number; position: number },
  ) => api.put(`/bidhi-line-image-association/${associationId}/bhed-position`, dto),
  deleteBhedPosition: (associationId: number, bidhiLineBhedId: number) =>
    api.delete(`/bidhi-line-image-association/${associationId}/bhed-position/${bidhiLineBhedId}`),
};

export type StyleTemplateThemeMode = 'lite' | 'dark';

export type PujaStyleTemplate = {
  id: number;
  name: string;
  styles: BookDefaultStylesV1 & {
    bookOverrides?: Record<string, any>;
    bookTemplateAssignments?: Record<string, number>;
  };
  isActive?: boolean;
  themeMode?: StyleTemplateThemeMode;
  updatedAt?: string;
};

export type PujaStyleTemplatesListResponse = {
  templates: PujaStyleTemplate[];
  activeTemplate?: PujaStyleTemplate | null;
  /** @deprecated Use activeLiteTemplateId — kept for older API responses. */
  activeTemplateId?: number | null;
  activeLiteTemplateId?: number | null;
  activeDarkTemplateId?: number | null;
};

export type PujaStyleTemplateUpdatePayload = {
  name?: string;
  styles?: PujaStyleTemplate['styles'];
  isActive?: boolean;
  themeMode?: StyleTemplateThemeMode;
  /** When isActive is true, which reader mode slot to assign (lite or dark). */
  activeFor?: StyleTemplateThemeMode;
};

export const pujaStyleTemplatesApi = {
  list: () => api.get<PujaStyleTemplatesListResponse>('/puja-style-templates'),
  create: (payload: { name: string; styles: PujaStyleTemplate['styles']; themeMode?: StyleTemplateThemeMode }) =>
    api.post<PujaStyleTemplate>('/puja-style-templates', payload),
  update: (id: number, payload: PujaStyleTemplateUpdatePayload) =>
    api.put<PujaStyleTemplate>(`/puja-style-templates/${id}`, payload),
  delete: (id: number) => api.delete<{ ok: true }>(`/puja-style-templates/${id}`),
};

// BidhiTag API Calls
export const bidhiTagAPI = {
  getAllBidhi: () => api.get('/bidhi-tag'),
  getBidhiById: (id: number) => api.get(`/bidhi-tag/${id}`),
  createTag: (tag: { name: string }) => api.post('/bidhi-tag', tag),
  updateTag: (id: number, tag: { name: string }) => api.patch(`/bidhi-tag/${id}`, tag),
  deleteTag: (id: number) => api.delete(`/bidhi-tag/${id}`),
  associateTagWithBidhi: (tagId: number, bidhiId: number) => api.post(`/bidhi-tag/${tagId}/bidhi/${bidhiId}`),
  removeTagFromBidhi: (tagId: number, bidhiId: number) => api.delete(`/bidhi-tag/${tagId}/bidhi/${bidhiId}`),
};

export type TagsCatalogResponse = {
  bidhiTags?: string[];
  bidhiLineTags?: string[];
  granthaTags?: string[];
  granthaLineTags?: string[];
};

export const tagsApi = {
  getAll: () => api.get<TagsCatalogResponse>('/tags'),
};

// Sankalpa API Calls
export const sankalpaApi = {
  getAll: () => api.get<Sankalpa[]>('/sankalpa'),
  getById: (id: number) => api.get<Sankalpa>(`/sankalpa/${id}`),
  create: (sankalpa: CreateSankalpa) => api.post<Sankalpa>('/sankalpa', sankalpa),
  update: (id: number, sankalpa: Partial<CreateSankalpa>) => api.patch<Sankalpa>(`/sankalpa/${id}`, sankalpa),
  delete: (id: number) => api.delete(`/sankalpa/${id}`),
  addLines: (id: number, lines: { content: string; order?: number; metadata?: Record<string, any> }[]) =>
    api.post(`/sankalpa/lines/${id}`, lines),
  updateLine: (lineId: number, data: { content: string; order?: number; metadata?: Array<{ key: string; value: unknown }> | Record<string, any> }) =>
    api.patch(`/sankalpa/lines/${lineId}`, data),
  deleteLine: (lineId: number) => api.delete(`/sankalpa/lines/${lineId}`),
};

// SankalpaTag API Calls
export const sankalpaTagAPI = {
  getAll: () => api.get('/sankalpa-tag'),
  getById: (id: number) => api.get(`/sankalpa-tag/${id}`),
  createTag: (tag: { name: string }) => api.post('/sankalpa-tag', tag),
  updateTag: (id: number, tag: { name: string }) => api.patch(`/sankalpa-tag/${id}`, tag),
  deleteTag: (id: number) => api.delete(`/sankalpa-tag/${id}`),
  associateTagWithSankalpa: (tagId: number, sankalpaId: number) => api.post(`/sankalpa-tag/${tagId}/sankalpa/${sankalpaId}`),
  removeTagFromSankalpa: (tagId: number, sankalpaId: number) => api.delete(`/sankalpa-tag/${tagId}/sankalpa/${sankalpaId}`),
};

export const brahmanYajamanKartabyaApi = {
  getAll: () => api.get<BrahmanYajamanKartabya[]>('/brahman-yajaman-kartabya'),
  getById: (id: number) => api.get<BrahmanYajamanKartabya>(`/brahman-yajaman-kartabya/${id}`),
  create: (item: CreateBrahmanYajamanKartabya) =>
    api.post<BrahmanYajamanKartabya>('/brahman-yajaman-kartabya', item),
  update: (id: number, item: Partial<CreateBrahmanYajamanKartabya>) =>
    api.patch<BrahmanYajamanKartabya>(`/brahman-yajaman-kartabya/${id}`, item),
  delete: (id: number) => api.delete(`/brahman-yajaman-kartabya/${id}`),
  addLines: (
    id: number,
    lines: { content: string; order?: number; metadata?: Record<string, any> }[],
  ) => api.post(`/brahman-yajaman-kartabya/lines/${id}`, lines),
  updateLine: (
    lineId: number,
    data: {
      content: string;
      order?: number;
      metadata?: Array<{ key: string; value: unknown }> | Record<string, any>;
    },
  ) => api.patch(`/brahman-yajaman-kartabya/lines/${lineId}`, data),
  deleteLine: (lineId: number) => api.delete(`/brahman-yajaman-kartabya/lines/${lineId}`),
};

export const brahmanYajamanKartabyaTagAPI = {
  getAll: () => api.get('/brahman-yajaman-kartabya-tag'),
  getById: (id: number) => api.get(`/brahman-yajaman-kartabya-tag/${id}`),
  createTag: (tag: { name: string }) => api.post('/brahman-yajaman-kartabya-tag', tag),
  updateTag: (id: number, tag: { name: string }) =>
    api.patch(`/brahman-yajaman-kartabya-tag/${id}`, tag),
  deleteTag: (id: number) => api.delete(`/brahman-yajaman-kartabya-tag/${id}`),
  associateTagWithBrahmanYajamanKartabya: (tagId: number, brahmanYajamanKartabyaId: number) =>
    api.post(
      `/brahman-yajaman-kartabya-tag/${tagId}/brahman-yajaman-kartabya/${brahmanYajamanKartabyaId}`,
    ),
  removeTagFromBrahmanYajamanKartabya: (tagId: number, brahmanYajamanKartabyaId: number) =>
    api.delete(
      `/brahman-yajaman-kartabya-tag/${tagId}/brahman-yajaman-kartabya/${brahmanYajamanKartabyaId}`,
    ),
};

// Puja API Calls
export const pujaApi = {
  getAll: () => api.get<Puja[]>('/puja'),
  getById: (id: number) => api.get<Puja>(`/puja/${id}`),
  create: (puja: CreatePuja | FormData) => {
    if (puja instanceof FormData) {
      return api.post<Puja>('/puja', puja, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
    }
    return api.post<Puja>('/puja', puja);
  },
  update: (id: number, puja: Partial<CreatePuja> | FormData) => {
    if (puja instanceof FormData) {
      return api.patch<Puja>(`/puja/${id}`, puja, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
    }
    return api.patch<Puja>(`/puja/${id}`, puja);
  },
  delete: (id: number) => api.delete(`/puja/${id}`),
  addLines: (id: number, lines: { content: string; order?: number; metadata?: Record<string, string> }[]) =>
    api.post(`/puja/line/${id}`, lines),
  updateLine: (lineId: number, data: { content: string; order?: number; metadata?: Record<string, string>; isVisible?: boolean }) =>
    api.patch(`/puja/line/${lineId}`, data),
  deleteLine: (lineId: number) => api.delete(`/puja/line/${lineId}`),
  bulkUpdateLineOrder: (updates: { pujaLineId: number; changedOrder: number }[]) =>
    api.patch('/puja/lines/bulk-order', updates),
};

// PujaTag API Calls
export const pujaTagAPI = {
  getAll: () => api.get('/puja-tag'),
  getById: (id: number) => api.get(`/puja-tag/${id}`),
  createTag: (tag: { name: string }) => api.post('/puja-tag', tag),
  updateTag: (id: number, tag: { name: string }) => api.patch(`/puja-tag/${id}`, tag),
  deleteTag: (id: number) => api.delete(`/puja-tag/${id}`),
  associateTagWithPuja: (tagId: number, pujaId: number) => api.post(`/puja-tag/${tagId}/puja/${pujaId}`),
  removeTagFromPuja: (tagId: number, pujaId: number) => api.delete(`/puja-tag/${tagId}/puja/${pujaId}`),
};

// Puja Line Link API Calls
export const pujaLineLinkApi = {
  // Bidhi links
  createBidhiLink: (payload: { pujaLineId: number; bidhiId: number; position: number; order: number; entireBidhi?: boolean; selectedBidhiLineIds?: number[]; titleVisible?: boolean; shortcut?: boolean; displayName?: string }) =>
    api.post('/puja-line-link/bidhi', payload),
  getBidhiLinksByPujaLine: (pujaLineId: number) =>
    api.get(`/puja-line-link/bidhi/puja-line/${pujaLineId}`),
  deleteBidhiLink: (id: number) =>
    api.delete(`/puja-line-link/bidhi/${id}`),
  bulkUpdateBidhiLinkOrder: (updates: { pujaBidhiLinkId: number; changedOrder: number }[]) =>
    api.patch('/puja-line-link/bidhi/bulk-order', updates),

  // Sankalpa links
  createSankalpaLink: (payload: { pujaLineId: number; sankalpaId: number; position: number; selectedSankalpaLineIds?: number[]; titleVisible?: boolean }) =>
    api.post('/puja-line-link/sankalpa', payload),
  getSankalpaLinksByPujaLine: (pujaLineId: number) =>
    api.get(`/puja-line-link/sankalpa/puja-line/${pujaLineId}`),
  deleteSankalpaLink: (id: number) =>
    api.delete(`/puja-line-link/sankalpa/${id}`),

  // Grantha links
  createGranthaLink: (payload: { pujaLineId: number; granthaId: number; startingPosition: number; shortcut?: boolean }) =>
    api.post('/puja-line-link/grantha', payload),
  deleteGranthaLink: (id: number) =>
    api.delete(`/puja-line-link/grantha/${id}`),
};

// Bidhi Line Link API Calls (multiple bidhis per line position)
export const bidhiLineLinkApi = {
  createMultipleBidhiLink: (payload: {
    bidhiLineId: number;
    bidhiId: number;
    position: number;
    order: number;
    entireBidhi?: boolean;
    selectedBidhiLineIds?: number[];
    titleVisible?: boolean;
    shortcut?: boolean;
    displayName?: string;
  }) => api.post('/bidhi-line-link/multiple-bidhi', payload),
  getMultipleBidhiLinksByBidhiLine: (bidhiLineId: number) =>
    api.get(`/bidhi-line-link/multiple-bidhi/bidhi-line/${bidhiLineId}`),
  updateMultipleBidhiLink: (id: number, payload: Partial<{
    bidhiLineId: number;
    bidhiId: number;
    position: number;
    order: number;
    entireBidhi: boolean;
    selectedBidhiLineIds: number[];
    titleVisible: boolean;
    shortcut: boolean;
    displayName: string;
  }>) => api.patch(`/bidhi-line-link/multiple-bidhi/${id}`, payload),
  bulkUpdateMultipleBidhiLinkOrder: (updates: { bidhiLineMultipleBidhiLinkId: number; changedOrder: number }[]) =>
    api.patch('/bidhi-line-link/multiple-bidhi/bulk-order', updates),
  deleteMultipleBidhiLink: (id: number) =>
    api.delete(`/bidhi-line-link/multiple-bidhi/${id}`),

  createSankalpaLink: (payload: {
    bidhiLineId: number;
    sankalpaId: number;
    position: number;
    entireSankalpa?: boolean;
    selectedSankalpaLineIds?: number[];
    titleVisible?: boolean;
  }) => api.post('/bidhi-line-link/sankalpa', payload),
  getSankalpaLinksByBidhiLine: (bidhiLineId: number) =>
    api.get(`/bidhi-line-link/sankalpa/bidhi-line/${bidhiLineId}`),
  updateSankalpaLink: (id: number, payload: Partial<{
    bidhiLineId: number;
    sankalpaId: number;
    position: number;
    entireSankalpa: boolean;
    selectedSankalpaLineIds: number[];
    titleVisible: boolean;
  }>) => api.patch(`/bidhi-line-link/sankalpa/${id}`, payload),
  deleteSankalpaLink: (id: number) =>
    api.delete(`/bidhi-line-link/sankalpa/${id}`),

  createGranthaLink: (payload: CreateBidhiLineGranthaLinkPayload) =>
    api.post<BidhiLineGranthaLink>('/bidhi-line-link/grantha', payload),
  getGranthaLinksByBidhiLine: (bidhiLineId: number) =>
    api.get<BidhiLineGranthaLink[]>(`/bidhi-line-link/grantha/bidhi-line/${bidhiLineId}`),
  updateGranthaLink: (
    id: number,
    payload: Partial<CreateBidhiLineGranthaLinkPayload & { granthaId: number; startingPosition: number; shortcut: boolean; ending_note: boolean; title: boolean; level_title: boolean; level_shloka_number: boolean; summary: boolean }>,
  ) => api.patch<BidhiLineGranthaLink>(`/bidhi-line-link/grantha/${id}`, payload),
  deleteGranthaLink: (id: number) => api.delete(`/bidhi-line-link/grantha/${id}`),

  createGranthaLevelLink: (payload: CreateBidhiLineGranthaLevelLinkPayload) =>
    api.post<BidhiLineGranthaLevelLink>('/bidhi-line-link/grantha-level', payload),
  getGranthaLevelLinksByBidhiLine: (bidhiLineId: number) =>
    api.get<BidhiLineGranthaLevelLink[]>(`/bidhi-line-link/grantha-level/bidhi-line/${bidhiLineId}`),
  updateGranthaLevelLink: (
    id: number,
    payload: Partial<CreateBidhiLineGranthaLevelLinkPayload & { granthaId: number; startingPosition: number; shortcut: boolean; level: string; levelId: number; ending_note: boolean; title: boolean; level_title: boolean; level_shloka_number: boolean; summary: boolean }>,
  ) => api.patch<BidhiLineGranthaLevelLink>(`/bidhi-line-link/grantha-level/${id}`, payload),
  deleteGranthaLevelLink: (id: number) =>
    api.delete(`/bidhi-line-link/grantha-level/${id}`),
};

// Samagri API Calls
export const samagriApi = {
  getAll: () => api.get<Samagri[]>('/karmakanda/samagri'),
  create: (samagri: CreateSamagri | FormData) => {
    if (samagri instanceof FormData) {
      return api.post<Samagri>('/karmakanda/samagri', samagri, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
    }
    return api.post<Samagri>('/karmakanda/samagri', samagri);
  },
  update: (id: number, samagri: Partial<CreateSamagri> | FormData) => {
    if (samagri instanceof FormData) {
      return api.patch<Samagri>(`/karmakanda/samagri/${id}`, samagri, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
    }
    return api.patch<Samagri>(`/karmakanda/samagri/${id}`, samagri);
  },
  delete: (id: number) => api.delete(`/karmakanda/samagri/${id}`),
};

// SamagriTag API Calls
export const samagriTagAPI = {
  getAll: () => api.get('/samagri-tag'),
  getById: (id: number) => api.get(`/samagri-tag/${id}`),
  createTag: (tag: { name: string }) => api.post('/samagri-tag', tag),
  updateTag: (id: number, tag: { name: string }) => api.patch(`/samagri-tag/${id}`, tag),
  deleteTag: (id: number) => api.delete(`/samagri-tag/${id}`),
  associateTagWithSamagri: (tagId: number, samagriId: number) =>
    api.post(`/samagri-tag/${tagId}/samagri/${samagriId}`),
  removeTagFromSamagri: (tagId: number, samagriId: number) =>
    api.delete(`/samagri-tag/${tagId}/samagri/${samagriId}`),
};

// Bidhi-Samagri Association API Calls
export const bidhiSamagriAssociationApi = {
  create: (payload: { bidhiId: number; samagriId: number; quantity: number; unit: string }) =>
    api.post('/bidhi-samagri-association', payload),
  update: (id: number, payload: { quantity?: number; unit?: string }) =>
    api.patch(`/bidhi-samagri-association/${id}`, payload),
  delete: (id: number) =>
    api.delete(`/bidhi-samagri-association/${id}`),
};

export const bidhiBrahmanYajamanKartabyaAssociationApi = {
  create: (payload: { bidhiId: number; brahmanYajamanKartabyaId: number }) =>
    api.post('/bidhi-brahman-yajaman-kartabya-association', payload),
  update: (id: number, payload: { bidhiId?: number; brahmanYajamanKartabyaId?: number }) =>
    api.patch(`/bidhi-brahman-yajaman-kartabya-association/${id}`, payload),
  delete: (id: number) =>
    api.delete(`/bidhi-brahman-yajaman-kartabya-association/${id}`),
};

// Puja-Samagri Association API Calls
export const pujaSamagriAssociationApi = {
  create: (payload: { pujaId: number; samagriId: number; quantity: number; unit?: string }) =>
    api.post('/puja-samagri-association', payload),
  update: (id: number, payload: { quantity?: number; unit?: string }) =>
    api.patch(`/puja-samagri-association/${id}`, payload),
  delete: (id: number) =>
    api.delete(`/puja-samagri-association/${id}`),
};

// Timing API Calls (grantha / bidhi / approved-to-display)
const asTimingList = <T,>(data: unknown): T[] => {
  if (Array.isArray(data)) return data as T[];
  if (data && typeof data === 'object') {
    const obj = data as Record<string, unknown>;
    if (Array.isArray(obj.data)) return obj.data as T[];
    if (Array.isArray(obj.items)) return obj.items as T[];
  }
  return [];
};

export type TimingCountrySettingPayload = {
  country: string;
  instructions?: string;
  isCountryHoliday?: boolean;
};

export type GranthaTimingPayload = {
  granthaId: number;
  title?: string;
  config: TimingConfig;
  countries?: string[];
  countrySettings?: TimingCountrySettingPayload[];
  instructions?: string;
};

export type BidhiTimingPayload = {
  bidhiId: number;
  title?: string;
  config: TimingConfig;
  countries?: string[];
  countrySettings?: TimingCountrySettingPayload[];
  instructions?: string;
};

export type ApprovedToDisplayPayload = {
  sourceType: 'grantha' | 'bidhi' | 'festival';
  sourceTimingId: number | string;
  year: number;
  granthaId?: number | null;
  bidhiId?: number | null;
  festivalId?: number | null;
  title?: string;
  config: TimingConfig;
  instructions?: string | null;
  sortOrder?: number;
  isHoliday?: boolean;
  /** Year-scoped; independent of source festival/timing. */
  countries?: string[];
  countrySettings?: TimingCountrySettingPayload[];
};

export const granthaTimingApi = {
  getAll: async (granthaId?: number) => {
    const response = await api.get('/grantha-timing', {
      params: granthaId != null ? { granthaId } : undefined,
    });
    return { ...response, data: asTimingList<BookTiming>(response.data) };
  },
  getByGranthaId: async (granthaId: number) => {
    const response = await api.get(`/grantha-timing/grantha/${granthaId}`);
    return { ...response, data: asTimingList<BookTiming>(response.data) };
  },
  getById: (id: number | string) => api.get<BookTiming>(`/grantha-timing/${id}`),
  create: (payload: GranthaTimingPayload) =>
    api.post<BookTiming>('/grantha-timing', payload),
  update: (id: number | string, payload: Partial<GranthaTimingPayload>) =>
    api.patch<BookTiming>(`/grantha-timing/${id}`, payload),
  delete: (id: number | string) => api.delete(`/grantha-timing/${id}`),
};

export const bidhiTimingApi = {
  getAll: async (bidhiId?: number) => {
    const response = await api.get('/bidhi-timing', {
      params: bidhiId != null ? { bidhiId } : undefined,
    });
    return { ...response, data: asTimingList<BidhiTiming>(response.data) };
  },
  getByBidhiId: async (bidhiId: number) => {
    const response = await api.get(`/bidhi-timing/bidhi/${bidhiId}`);
    return { ...response, data: asTimingList<BidhiTiming>(response.data) };
  },
  getById: (id: number | string) => api.get<BidhiTiming>(`/bidhi-timing/${id}`),
  create: (payload: BidhiTimingPayload) =>
    api.post<BidhiTiming>('/bidhi-timing', payload),
  update: (id: number | string, payload: Partial<BidhiTimingPayload>) =>
    api.patch<BidhiTiming>(`/bidhi-timing/${id}`, payload),
  delete: (id: number | string) => api.delete(`/bidhi-timing/${id}`),
};

export type ApprovedYearSettings = {
  year: number;
  isPublicVisible: boolean;
};

export const approvedToDisplayApi = {
  /** Public listing (empty when Public Visibility is off for the year). */
  getAll: async (year?: number) => {
    const response = await api.get('/approved-to-display', {
      params: year != null ? { year } : undefined,
    });
    return { ...response, data: asTimingList<ApprovedTiming>(response.data) };
  },
  /** Admin listing — always returns real approved rows. */
  getAllAdmin: async (year?: number) => {
    const response = await api.get('/approved-to-display/admin', {
      params: year != null ? { year } : undefined,
    });
    return { ...response, data: asTimingList<ApprovedTiming>(response.data) };
  },
  getYearSettings: (year: number) =>
    api.get<ApprovedYearSettings>(`/approved-to-display/year-settings/${year}`),
  setYearPublicVisible: (year: number, isPublicVisible: boolean) =>
    api.put<ApprovedYearSettings>(`/approved-to-display/year-settings/${year}`, {
      isPublicVisible,
    }),
  getById: (id: number | string) =>
    api.get<ApprovedTiming>(`/approved-to-display/${id}`),
  create: (payload: ApprovedToDisplayPayload) =>
    api.post<ApprovedTiming>('/approved-to-display', payload),
  update: (id: number | string, payload: Partial<ApprovedToDisplayPayload>) =>
    api.patch<ApprovedTiming>(`/approved-to-display/${id}`, payload),
  reorder: (orderedIds: Array<number | string>) =>
    api.patch('/approved-to-display/reorder', { orderedIds }),
  delete: (id: number | string) => api.delete(`/approved-to-display/${id}`),
};

export type {
  CreatePopularBooksBidhiPayload,
  PopularBooksBidhi,
  UpdatePopularBooksBidhiPayload,
} from '../types/popularBooksBidhi';

export const popularBooksBidhiApi = {
  list: () => api.get<PopularBooksBidhi[]>('/popular-books-bidhi'),
  getById: (id: number) => api.get<PopularBooksBidhi>(`/popular-books-bidhi/${id}`),
  create: (payload: CreatePopularBooksBidhiPayload) =>
    api.post<PopularBooksBidhi>('/popular-books-bidhi', payload),
  update: (id: number, payload: UpdatePopularBooksBidhiPayload) =>
    api.patch<PopularBooksBidhi>(`/popular-books-bidhi/${id}`, payload),
  reorder: (orderedIds: number[]) =>
    api.patch<PopularBooksBidhi[]>('/popular-books-bidhi/reorder', { orderedIds }),
  delete: (id: number) => api.delete(`/popular-books-bidhi/${id}`),
};

export type FestivalPayload = {
  name: string;
  sourceKey?: string | null;
  tag?: string[];
  description?: string | null;
  isHoliday?: boolean;
  classification?: string;
  /** Existing filenames to keep, in display order. */
  images?: string[];
};

/** Public URL for a festival image filename. */
export const festivalImageUrl = (filename: string): string =>
  `${API_URL}/uploads/festival-images/${encodeURIComponent(filename)}`;

/** Build multipart body for festival create/update (with optional uploads). */
export const buildFestivalFormData = (input: {
  name: string;
  sourceKey?: string | null;
  description?: string | null;
  classification?: string;
  isHoliday?: boolean;
  tag?: string[];
  /** Kept filenames in display order (omit a name to delete it). */
  images?: string[];
  /** New files appended after `images`. */
  imageFiles?: File[];
}): FormData => {
  const formData = new FormData();
  formData.append('name', input.name);
  formData.append('sourceKey', input.sourceKey ?? '');
  formData.append('description', input.description ?? '');
  formData.append(
    'classification',
    input.classification != null && input.classification !== ''
      ? input.classification
      : 'festival'
  );
  formData.append('isHoliday', input.isHoliday ? 'true' : 'false');
  formData.append('tag', JSON.stringify(input.tag || []));
  formData.append('images', JSON.stringify(input.images || []));
  (input.imageFiles || []).forEach((file) => {
    formData.append('imageFiles', file);
  });
  return formData;
};

export type FestivalTimingPayload = {
  festivalId: number;
  config: TimingConfig;
  notifyBefore?: number | null;
  notificationLat?: string | null;
  notificationLng?: string | null;
  radius?: number | null;
  locationSummary?: string | null;
  countries?: string[];
  countrySettings?: TimingCountrySettingPayload[];
  instructions?: string | null;
  notificationBoundary?: FestivalNotificationPoint[] | null;
};

export type FestivalBhedPayload = {
  festivalId: number;
  name: string;
  languageId: number;
  description?: string | null;
};

export const festivalApi = {
  getAll: async () => {
    const response = await api.get('/festival');
    return { ...response, data: asTimingList<Festival>(response.data) };
  },
  getById: (id: number | string) => api.get<Festival>(`/festival/${id}`),
  create: (payload: FestivalPayload | FormData) => {
    if (payload instanceof FormData) {
      return api.post<Festival>('/festival', payload, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    }
    return api.post<Festival>('/festival', payload);
  },
  update: (id: number | string, payload: Partial<FestivalPayload> | FormData) => {
    if (payload instanceof FormData) {
      return api.patch<Festival>(`/festival/${id}`, payload, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    }
    return api.patch<Festival>(`/festival/${id}`, payload);
  },
  delete: (id: number | string) => api.delete(`/festival/${id}`),
};

export const festivalTimingApi = {
  getAll: async (festivalId?: number) => {
    const response = await api.get('/festival-timing', {
      params: festivalId != null ? { festivalId } : undefined,
    });
    return { ...response, data: asTimingList<FestivalTiming>(response.data) };
  },
  getByFestivalId: async (festivalId: number) => {
    const response = await api.get(`/festival-timing/festival/${festivalId}`);
    return { ...response, data: asTimingList<FestivalTiming>(response.data) };
  },
  getById: (id: number | string) =>
    api.get<FestivalTiming>(`/festival-timing/${id}`),
  create: (payload: FestivalTimingPayload) =>
    api.post<FestivalTiming>('/festival-timing', payload),
  update: (id: number | string, payload: Partial<FestivalTimingPayload>) =>
    api.patch<FestivalTiming>(`/festival-timing/${id}`, payload),
  delete: (id: number | string) => api.delete(`/festival-timing/${id}`),
};

export const festivalBhedApi = {
  getAll: async (festivalId?: number) => {
    const response = await api.get('/festival-bhed', {
      params: festivalId != null ? { festivalId } : undefined,
    });
    return { ...response, data: asTimingList<FestivalBhed>(response.data) };
  },
  getByFestivalId: async (festivalId: number) => {
    const response = await api.get(`/festival-bhed/festival/${festivalId}`);
    return { ...response, data: asTimingList<FestivalBhed>(response.data) };
  },
  getById: (id: number | string) => api.get<FestivalBhed>(`/festival-bhed/${id}`),
  create: (payload: FestivalBhedPayload) =>
    api.post<FestivalBhed>('/festival-bhed', payload),
  update: (id: number | string, payload: Partial<FestivalBhedPayload>) =>
    api.patch<FestivalBhed>(`/festival-bhed/${id}`, payload),
  delete: (id: number | string) => api.delete(`/festival-bhed/${id}`),
};

export type FestivalAssociationPayload = {
  sourceType: 'bidhi' | 'grantha' | 'festival';
  sourceId: number;
  timingId: number;
};

export const festivalAssociationApi = {
  getByFestivalId: async (festivalId: number) => {
    const response = await api.get(`/festival/${festivalId}/associations`);
    return { ...response, data: asTimingList<FestivalAssociation>(response.data) };
  },
  associate: async (festivalId: number, payload: FestivalAssociationPayload) => {
    const response = await api.post(`/festival/${festivalId}/associations`, payload);
    return { ...response, data: asTimingList<FestivalAssociation>(response.data) };
  },
  disassociate: async (festivalId: number, payload: FestivalAssociationPayload) => {
    const response = await api.delete(`/festival/${festivalId}/associations`, {
      data: payload,
    });
    return { ...response, data: asTimingList<FestivalAssociation>(response.data) };
  },
};

// Panchanga API Calls
const panchangaApiInstance = axios.create({
  baseURL: 'https://patrocalcdev.vag.org.np/',
});

export type PanchangaYearDay = {
  date: string;
  bs_year: number;
  bs_month: number;
  day_number: number;
  tithi_year?: number;
  is_requested_month?: boolean;
  tithi_name?: string;
  tithi_index?: number;
  pakshya?: string;
  pakshya_name?: string;
  nakshatra_name?: string;
  yoga_name?: string;
  weekday?: string;
  bs_lunar_month_index?: number;
  lunar_month_index?: number;
  [key: string]: unknown;
};

export type PanchangaYearResponse = {
  days?: PanchangaYearDay[];
  total_days?: number;
  year?: number;
  [key: string]: unknown;
};

const DEFAULT_PANCHANGA_LOCATION = {
  lat: 27.70055,
  lon: 83.44836,
  timezone: 'Asia/Kathmandu',
  mode: 'ne',
} as const;

export const panchangaApi = {
  get: (params: { mode: string; lat: number; lon: number; timezone: string; bs_date: string; time: string }) =>
    panchangaApiInstance.get<Panchanga>('/api/v2/sankalpa', { params }),

  /** One BS month of year panchanga (call months 1–12 for a full year). */
  getYearMonth: (params: {
    bs_year: number;
    bs_month: number;
    lat?: number;
    lon?: number;
    timezone?: string;
    mode?: string;
  }) =>
    panchangaApiInstance.get<PanchangaYearResponse>('/api/v2/panchanga/year', {
      params: {
        ...DEFAULT_PANCHANGA_LOCATION,
        ...params,
      },
    }),

  /**
   * Elib month panchanga, including kaala windows under `coordinates.day_length_based_periods`.
   * GET /panchanga?year=&month=&timezone=&lat=&lng=
   */
  getElibMonth: async (params: {
    bs_year: number;
    bs_month: number;
    lat?: number;
    lon?: number;
    lng?: number;
    timezone?: string;
    mode?: string;
  }) => {
    const response = await api.get<ElibPanchangaRow[]>('/panchanga', {
      params: {
        year: params.bs_year,
        month: params.bs_month,
        timezone: params.timezone ?? DEFAULT_PANCHANGA_LOCATION.timezone,
        lat: params.lat ?? DEFAULT_PANCHANGA_LOCATION.lat,
        lng: params.lng ?? params.lon ?? DEFAULT_PANCHANGA_LOCATION.lon,
        mode: params.mode ?? DEFAULT_PANCHANGA_LOCATION.mode,
      },
    });
    const rows: ElibPanchangaRow[] = unwrapApiList<ElibPanchangaRow>(response.data);
    const finalRows = rows.length > 0 ? rows : (Array.isArray(response.data) ? response.data : []);
    return {
      ...response,
      data: {
        days: finalRows.map(mapElibPanchangaRow),
        year: params.bs_year,
      } satisfies PanchangaYearResponse,
    };
  },
};

/**
 * Construct public media URL from storedName or return direct URL if available.
 * Uploads are publicly accessible without authentication tokens.
 */
export const resolveMediaUrl = (storedName?: string, directUrl?: string): string => {
  if (directUrl && directUrl.trim()) {
    return directUrl;
  }
  if (!storedName) return '';
  const cleanStoredName = storedName.replace(/^\/+/, '');
  const cleanBase = API_URL.replace(/\/+$/, '');
  return `${cleanBase}/uploads/media/${cleanStoredName}`;
};

export const mediaApi = {
  /**
   * List all media files. Note: GET /media list does not contain full 'url',
   * so resolveMediaUrl(item.storedName) or getById can be used to resolve the URL.
   */
  getAll: (params?: { groupId?: number | string; search?: string }) =>
    api.get<unknown>('/media', { params }).then((res) => {
      const unwrapped = unwrapApiList<Media>(res.data);
      const data: Media[] = unwrapped.length > 0 ? unwrapped : (Array.isArray(res.data) ? res.data : []);
      return { ...res, data };
    }),

  /** Get single media item including resolved 'url'. */
  getById: (id: number | string) =>
    api.get<Media>(`/media/${id}`),

  /**
   * Upload media file via multipart/form-data.
   * DO NOT set Content-Type header manually — the browser must set it with boundary.
   */
  upload: (formData: FormData, onUploadProgress?: (progressEvent: any) => void) =>
    api.post<Media>('/media', formData, {
      onUploadProgress,
    }),

  /** Update media metadata (name, groupId, description) */
  update: (id: number | string, data: UpdateMediaDto) =>
    api.patch<Media>(`/media/${id}`, data),

  /** Delete media item */
  delete: (id: number | string) =>
    api.delete(`/media/${id}`),
};

export const mediaGroupApi = {
  /** List all media groups */
  getAll: () =>
    api.get<unknown>('/media-groups').then((res) => {
      const unwrapped = unwrapApiList<MediaGroup>(res.data);
      const data: MediaGroup[] = unwrapped.length > 0 ? unwrapped : (Array.isArray(res.data) ? res.data : []);
      return { ...res, data };
    }),

  /** Get group by ID */
  getById: (id: number | string) =>
    api.get<MediaGroup>(`/media-groups/${id}`),

  /** Primary consumer-facing endpoint: returns group and all files with resolved URLs */
  getByName: (name: string) =>
    api.get<MediaGroup>(`/media-groups/by-name/${encodeURIComponent(name)}`),

  /** Create media group */
  create: (data: CreateMediaGroupDto) =>
    api.post<MediaGroup>('/media-groups', data),

  /** Update media group */
  update: (id: number | string, data: UpdateMediaGroupDto) =>
    api.patch<MediaGroup>(`/media-groups/${id}`, data),

  /** Delete media group */
  delete: (id: number | string) =>
    api.delete(`/media-groups/${id}`),
};

export { mantraJapaApi, mantraJapaFolderApi } from './mantraJapaApi';
export { dailyShlokaApi } from './dailyShlokaApi';